// ExpandedHistoryPanel.jsx
import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { 
    Search, X, Trash2, Download, CheckSquare, Square, Loader, EyeOff
} from 'lucide-react';
import { useConversation } from '@/managers/ConvoManager';
import { botManager } from '@/managers/BotManager';
import { convertToDate, formatDate } from '@/utils/timeUtils';
// Worker-based search hook and debounce are no longer needed

const useBot = () => {
    const [botState, setBotState] = useState(botManager.getState());
    useEffect(() => {
        const unsubscribe = botManager.subscribe(setBotState);
        return unsubscribe;
    }, []);
    return { bots: botState.allBots };
};


const HistoryTableRow = ({ 
    conversation, 
    isSelected, 
    onSelect, 
    onDelete,
    onClick,
}) => {
    const messageCount = conversation?.messageCount || 0;
    const isIncognito = conversation?.privacyMode === 'incognito';
    const truncatedBotName = (conversation?.botName?.length || 0) > 20
        ? `${conversation.botName.slice(0, 20)}...` 
        : conversation?.botName || 'Unknown Bot';

    return (
        <div 
            className={`
                grid grid-cols-[40px,2fr,1fr,80px,150px,40px] gap-4 px-4 py-3
                hover:bg-surface-dark/80 cursor-pointer border-b
                border-[var(--border-subtle)] items-center transition-colors
                ${isSelected ? 'bg-surface-dark/60' : ''}
            `}
            onClick={onClick}
        >
            <div className="flex items-center justify-center">
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        onSelect(conversation.id);
                    }}
                    className="p-1 hover:bg-surface-dark rounded-md transition-colors"
                >
                    {isSelected ? (
                        <CheckSquare size={18} className="text-primary" />
                    ) : (
                        <Square size={18} className="text-on-surface-dim" />
                    )}
                </button>
            </div>

            <div className="flex flex-col min-w-0">
                <div className="flex items-center gap-2 min-w-0">
                    <div className="text-sm text-on-surface font-medium truncate">
                        {conversation?.name || 'New Chat'}
                    </div>
                    {isIncognito && (
                        <span className="inline-flex items-center gap-1 rounded-full border border-[var(--border-subtle)] px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-[0.08em] text-on-surface-dim">
                            <EyeOff size={10} /> Incognito
                        </span>
                    )}
                </div>
            </div>

            <div className="text-sm text-on-surface-dim truncate">
                {truncatedBotName}
            </div>

            <div className="text-sm text-on-surface-dim text-center">
                {messageCount}
            </div>

            <div className="text-sm text-on-surface-dim text-center">
                {formatDate(conversation?.lastUpdated)}
            </div>

            <div className="flex items-center justify-center">
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        onDelete(conversation.id);
                    }}
                    className="p-2 hover:bg-surface-dark/80 rounded-md transition-colors"
                >
                    <Trash2 size={16} className="text-on-surface-dim hover:text-error transition-colors" />
                </button>
            </div>
        </div>
    );
};

const ExpandedHistoryPanel = ({ isOpen, onClose }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [filterBot, setFilterBot] = useState('all');
    const [sortOrder, setSortOrder] = useState('newest');

    const { 
        conversations,
        deleteConversation,
        switchToConversation,
        isInitialized: isConvoManagerInitialized,
    } = useConversation();
    
    const { bots } = useBot();

    const processedConversations = useMemo(() => {
        let results = Object.values(conversations || {});
        const lowerCaseTerm = searchTerm.trim().toLowerCase();

        if (filterBot !== 'all') {
            results = results.filter(conv => conv?.botId === filterBot);
        }

        if (lowerCaseTerm) {
            results = results.filter(conv => 
                conv.name?.toLowerCase().includes(lowerCaseTerm)
            );
        }

        results.sort((a, b) => {
            const dateA = convertToDate(a?.lastUpdated) || new Date(0);
            const dateB = convertToDate(b?.lastUpdated) || new Date(0);
            return sortOrder === 'newest' 
                ? dateB.getTime() - dateA.getTime()
                : dateA.getTime() - dateB.getTime();
        });

        return results;
    }, [conversations, filterBot, searchTerm, sortOrder]);

    const toggleSelection = useCallback((id) => {
        setSelectedIds(prev => {
            const newSet = new Set(prev);
            newSet.has(id) ? newSet.delete(id) : newSet.add(id);
            return newSet;
        });
    }, []);

    const handleBulkDelete = useCallback(async () => {
        if (selectedIds.size === 0) return;
        
        const confirmationMessage = `Are you sure you want to delete ${selectedIds.size} conversation(s)?`;
        if (!window.confirm(confirmationMessage)) return;
        
        try {
            await Promise.all(Array.from(selectedIds).map(id => deleteConversation(id)));
            setSelectedIds(new Set());
        } catch (error) {
            console.error('Error during bulk delete:', error);
            alert('Some conversations could not be deleted.');
        }
    }, [selectedIds, deleteConversation]);
    
    const handleDeleteOne = useCallback(async (id) => {
        if (window.confirm('Are you sure you want to delete this conversation?')) {
            try {
                await deleteConversation(id);
                setSelectedIds(prev => {
                    const newSet = new Set(prev);
                    newSet.delete(id);
                    return newSet;
                });
            } catch (error) {
                console.error(`Error deleting conversation ${id}:`, error);
                alert('The conversation could not be deleted.');
            }
        }
    }, [deleteConversation]);

    const handleExport = useCallback(() => {
        if (selectedIds.size === 0) {
            alert("Please select conversations to export.");
            return;
        }
        try {
            const exportData = Array.from(selectedIds)
                .map(id => conversations?.[id])
                .filter(Boolean);
            
            if (exportData.length === 0) {
                 alert("No data found for selected conversations.");
                 return;
            }
            
            const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `chat-history-export-${new Date().toISOString()}.json`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error exporting conversations:', error);
            alert('Failed to export conversations.');
        }
    }, [selectedIds, conversations]);

    if (!isOpen) return null;
    
    const isLoading = !isConvoManagerInitialized && Object.keys(conversations || {}).length === 0;

    return (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-md z-[100]">
            <div className="w-[80%] h-[80vh] mx-auto mt-[10vh] flex flex-col p-6 
                          bg-surface rounded-xl border border-[var(--border-color)] shadow-2xl">
                <div className="flex items-center justify-between mb-6">
                    <h2 className="text-2xl font-semibold text-on-surface">Chat History</h2>
                    <button onClick={onClose} className="p-2 hover:bg-surface-dark/60 rounded-full transition-colors">
                        <X size={24} className="text-on-surface" />
                    </button>
                </div>

                <div className="flex items-center gap-4 mb-6">
                    <div className="flex-1 relative">
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Search by conversation title..."
                            className="w-full bg-surface-dark text-on-surface px-4 py-3 rounded-lg text-sm border border-[var(--border-subtle)] placeholder:text-on-surface-dim focus:outline-none focus:border-primary pr-10"
                        />
                         <div className="absolute right-4 top-3.5">
                            <Search className="w-4 h-4 text-on-surface-dim" />
                        </div>
                    </div>
                    <select
                        value={filterBot}
                        onChange={(e) => setFilterBot(e.target.value)}
                        className="bg-surface-dark text-on-surface px-4 py-2.5 rounded-lg text-sm border border-[var(--border-subtle)]"
                    >
                        <option value="all">All Bots</option>
                        {(bots || []).map(bot => <option key={bot.id} value={bot.id}>{bot.name}</option>)}
                    </select>
                    <button
                        onClick={() => setSortOrder(prev => prev === 'newest' ? 'oldest' : 'newest')}
                        className="px-4 py-2.5 hover:bg-surface-dark/60 rounded-lg flex items-center gap-2 text-sm text-on-surface border border-[var(--border-subtle)] transition-colors"
                    >
                        Sort: {sortOrder === 'newest' ? 'Newest' : 'Oldest'}
                    </button>
                </div>

                {selectedIds.size > 0 && (
                    <div className="flex items-center gap-4 mb-4 p-4 bg-surface-dark rounded-lg">
                        <span className="text-sm text-on-surface font-medium">{selectedIds.size} selected</span>
                        <div className="flex-1" />
                        <button onClick={handleExport} className="px-4 py-2 rounded-lg hover:bg-surface-raised text-sm text-on-surface flex items-center gap-2 border border-[var(--border-subtle)] transition-colors">
                            <Download size={16} /> Export
                        </button>
                        <button onClick={handleBulkDelete} className="px-4 py-2 rounded-lg hover:bg-error/10 text-sm text-error flex items-center gap-2 border border-error/20 transition-colors">
                            <Trash2 size={16} /> Delete
                        </button>
                    </div>
                )}

                <div className="flex-1 overflow-y-auto bg-surface-dark/30 rounded-lg border border-[var(--border-subtle)]">
                    <div className="grid grid-cols-[40px,2fr,1fr,80px,150px,40px] gap-4 px-4 py-3 border-b border-[var(--border-color)] bg-surface-dark/40 sticky top-0 z-10">
                        <div></div>
                        <span className="text-xs font-medium text-on-surface-dim uppercase">Conversation</span>
                        <span className="text-xs font-medium text-on-surface-dim uppercase">Bot Name</span>
                        <span className="text-xs font-medium text-on-surface-dim uppercase text-center">Messages</span>
                        <span className="text-xs font-medium text-on-surface-dim uppercase text-center">Last Activity</span>
                        <span className="text-xs font-medium text-on-surface-dim uppercase text-center">Delete</span>
                    </div>
                    {isLoading ? (
                        <div className="flex flex-col items-center justify-center h-[200px] gap-3">
                            <Loader size={24} className="animate-spin text-primary" />
                            <span className="text-on-surface-dim">Loading conversations...</span>
                        </div>
                    ) : processedConversations.length > 0 ? (
                        processedConversations.map(conversation => (
                            <HistoryTableRow
                                key={conversation?.id}
                                conversation={conversation}
                                isSelected={selectedIds.has(conversation?.id)}
                                onSelect={toggleSelection}
                                onDelete={handleDeleteOne}
                                onClick={() => switchToConversation(conversation.id).then(() => onClose())}
                            />
                        ))
                    ) : (
                        <div className="flex flex-col items-center justify-center h-[200px] gap-3">
                            <span className="text-on-surface-dim">
                                {searchTerm ? "No matching conversations found" : "No conversations available"}
                            </span>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ExpandedHistoryPanel;